// Implements: F-IDN-017, F-UIX-009, F-UIX-010
/**
 * The Mis datos section: owns the export download and the erasure of the own personal data.
 *
 * The export goes through `httpDownload` (a refusal is a notice, never a file holding an error
 * body) and is saved as `mis-datos-<slug>.json`. The erasure is sent once (`retry: false`,
 * checklist 13) after the dialog's e-mail confirmation; on success the hook empties the cache and
 * lands on `/login`, and the toast says the data are gone. A refusal reaches the dialog as its
 * reason (`eraseRefusalOf`), so "you still own mockups" never reads as "try again".
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { saveBlob } from "../../../shared/ui/saveBlob";
import { EraseAccountDialog } from "../components/EraseAccountDialog";
import { PersonalDataPanel } from "../components/PersonalDataPanel";
import { useMeQuery } from "../hooks/useMe";
import { usePersonalData } from "../hooks/usePersonalData";
import { eraseRefusalOf } from "../model/eraseRefusal";
import { exportFileName } from "../model/exportName";

export function PersonalDataContainer(): ReactElement {
  const { t } = useTranslation();
  const { download, erase } = usePersonalData();
  // Once the erasure is on its way the profile is never asked for again: it no longer exists.
  const me = useMeQuery(erase.isIdle || erase.isError);
  const { push } = useToast();
  const [exportFailed, setExportFailed] = useState(false);
  const [asking, setAsking] = useState(false);

  if (me.isPending) return <LoadingState label={t("common.state.loading")} />;
  if (me.isError) {
    return (
      <ErrorState
        error={me.error}
        title={t("account.profile.loadError")}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  const profile = me.data;
  return (
    <>
      <PersonalDataPanel
        downloading={download.isPending}
        exportFailed={exportFailed}
        onDownload={() => {
          setExportFailed(false);
          download.mutate(profile.id, {
            onSuccess: ({ blob }) => {
              saveBlob(blob, exportFileName(profile.full_name));
              push({ tone: "success", message: t("account.data.downloading") });
            },
            onError: () => {
              setExportFailed(true);
            },
          });
        }}
        onErase={() => {
          erase.reset();
          setAsking(true);
        }}
      />
      <EraseAccountDialog
        key={profile.id}
        open={asking}
        email={profile.email}
        busy={erase.isPending}
        refusal={eraseRefusalOf(erase.error)}
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          erase.mutate(profile.id, {
            onSuccess: () => {
              push({ tone: "success", message: t("account.data.erased") });
            },
          });
        }}
      />
    </>
  );
}
